import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import numeral from 'numeral'
import {
  forwardRef,
  Fragment,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  DeepMap,
  FieldError,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import {
  IconButton,
  Icons,
  ItemImageArea,
  PointDisplay,
  SkeletonText,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import {
  Collection,
  CollectionFeeDetailFragment,
  EscottMasterInput,
  EventCollectionBoxFragment,
  FeeDetailFragment,
  Maybe,
  PaymentDetailFragment,
  PaymentMethod,
  PersonalInformationType,
  PrepaidPointBalance,
  PurchasablePrepaidPointConnectionFragment,
  PurchasablePrepaidPointDetailFragment,
  UserAddressFragment,
  UserCartStoreItemProceedEventCollectionFragment,
  UserCartStoreItemPurchaseProceedFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ContentFrame } from '@/components/layouts/ContentFrame'
import { PointChargeDrawer } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import {
  StorageImage,
  tLabel,
  useForm,
  UseFormProps,
  useRouter,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'
import { DateTime } from '@/core/entities'
import { individualDisplayResponsive } from '@/core/individual'
import { setStoreProceed } from '@/core/native/storage'
import { useAnalytics } from '@/lib/analytics'
import { filterHiddenItem } from '@/lib/array'
import { dayjs } from '@/lib/dayjs'

import {
  calcurateIsJustNow,
  checkPurchaseLimitPerPickup,
  JustNowChip,
  ReceiveTimeModal,
  ReceiveTimeValue,
} from './ReceiveTimeModal'

export * from './fragments'

export type State = {
  loading: boolean
  cart: Maybe<UserCartStoreItemPurchaseProceedFragment> | undefined
  cartItems: UserCartStoreItemPurchaseProceedFragment['items']
  cartItemsByCollection:
    | {
        collection: Maybe<Pick<Collection, 'id' | 'title'>> | undefined
        items: UserCartStoreItemPurchaseProceedFragment['items']
        eventCollection:
          | UserCartStoreItemProceedEventCollectionFragment
          | undefined
      }[]
    | null
  fullName: string
  paymentSource: Maybe<PaymentDetailFragment> | undefined
  /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
  enablePointCarrierPayment: boolean
  address: Maybe<UserAddressFragment['address']>
  phoneNumber: string
  pointChargeLoading: boolean
  isPhoneNumberVerified: boolean
  systemFee: Maybe<CollectionFeeDetailFragment>[]
  shippingFee: Maybe<FeeDetailFragment>
  konbiniFee: Maybe<FeeDetailFragment>
  prepaidPointBalance:
    | Maybe<
        Pick<
          PrepaidPointBalance,
          | 'balance'
          | 'prepaidPointId'
          | 'formattedPrepaidPointBarance'
          | 'lastUpdatedAt'
        >
      >
    | undefined
  purchasablePrepaidPoints: Maybe<PurchasablePrepaidPointConnectionFragment>
  sendingVerification: boolean
  myPickupUsers: {
    pickupTime: number
    items: {
      itemId: string
      count: number
      collectionId: string
    }[]
  }[]
  exceededItemCount: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onPurchasePoint: (
    plan: PurchasablePrepaidPointDetailFragment,
    paymentMethod: Maybe<PaymentMethod>,
    escottInput: EscottMasterInput
  ) => Promise<void>
  onSendVerificationSMS: () => Promise<void>
}

const schema: UseFormProps['schema'] = o => ({
  purchaseType: o.string().label(tLabel('storeItemPurchaseProceed.type')),
  fullName: o
    .string()
    .when('type', {
      is: 'konbini',
      then: o.string(),
      otherwise: o.string().optional().allow(''),
    })
    .label(tLabel('storeItemPurchaseProceed.fullName')),
  receiptDateTimes: o
    .array()
    .items({
      collectionId: o.string().optional().allow(''),
      hasPickupItem: o.boolean().optional(),
      pickupDate: o
        .string()
        .label(tLabel('storeItemPurchaseProceed.receiptDateTime'))
        .when('hasPickupItem', {
          is: true,
          then: o.required(),
          otherwise: o.optional().allow(''),
        }),
      pickupTime: o
        .string()
        .label(tLabel('storeItemPurchaseProceed.receiptDateTime'))
        .when('hasPickupItem', {
          is: true,
          then: o.required(),
          otherwise: o.optional().allow(''),
        }),
    })
    .optional(),
})

type PurchaseType = 'cash' | 'konbini' | 'prepaid_point'

type StoreItemPurchaseValue = {
  purchaseType: PurchaseType
  fullName: string
  receiptDateTimes?: {
    collectionId: string
    pickupDate?: string
    pickupTime?: string
  }[]
}

export const StoreItemPurchaseProceedTemplate: Template<State, Action> = ({
  state,
  onPurchasePoint,
  onSendVerificationSMS,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')
  const { router, routerNames, generatePath, routerPages } =
    useRouter<{ cartId: string; q: string }>()
  // 受取時間のバリデーションエラーのコレクションIDを格納
  const [purchaseLimitPerPickupErrorIds, setPurchaseLimitPerPickupErrorIds] =
    useState<string[]>([])

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    register,
    formState: { isSubmitting, errors },
  } = useForm<StoreItemPurchaseValue>({
    schema,
    defaultValues: {
      purchaseType: 'prepaid_point',
      receiptDateTimes: [],
    },
  })
  const purchaseType = watch('purchaseType')

  // 個人情報の登録必須項目
  const personalInformationRequirements = useMemo<string[]>(() => {
    if (!state.cartItems) return []

    const result = state.cartItems.reduce((acc: string[], item) => {
      const requirements = item.item.personalInformationRequirements

      return Array.isArray(requirements) ? acc.concat(requirements) : acc
    }, [])

    return [...new Set(result)]
  }, [state.cartItems])

  const handleProceed = (value: StoreItemPurchaseValue) => {
    const { receiptDateTimes, ...rest } = value

    // 受け取り時間のフォーマットを修正
    const pickupTimes =
      receiptDateTimes
        ?.map(({ collectionId, pickupDate, pickupTime }) => ({
          collectionId,
          pickupTime: pickupTime
            ? dayjs(`${pickupDate} ${pickupTime}`).tz().format()
            : null,
        }))
        // 受取時間不要コレクションのpickupTimeを除外
        ?.filter(({ pickupTime }) => !!pickupTime) ?? null

    setStoreProceed({
      ...rest,
      pickupTimes,
    })

    router.replace(
      generatePath(routerPages.StoreItemPurchaseConfirmation, {
        cartId: state.cart?.id ?? '#',
      })
    )
  }

  const [openPointPurchaseDrawer, setOpenPointPurchaseDrawer] =
    useState<boolean>(false)

  const hasEnoughPoint = useMemo<boolean>(() => {
    const totalPriceFee = getTotalPriceFee({
      totalPrice: state.cart?.totalPrice,
      totalSystemFee:
        state.systemFee?.reduce((total, fee) => total + (fee?.fee ?? 0), 0) ??
        0,
      purchaseType,
      shippingFee: state.shippingFee?.fee,
      konbiniFee: state.konbiniFee?.fee,
    })
    return totalPriceFee <= (state.prepaidPointBalance?.balance ?? 0)
  }, [
    purchaseType,
    state.cart?.totalPrice,
    state.konbiniFee?.fee,
    state.prepaidPointBalance?.balance,
    state.shippingFee?.fee,
    state.systemFee,
  ])

  // 入力項目が不足し、次のステップに進めない状態
  const disableToNext = useMemo<boolean>(
    () =>
      // 氏名未登録
      (personalInformationRequirements.includes(
        PersonalInformationType.FullName
      ) &&
        !state.fullName) ||
      // 電番未認証
      (personalInformationRequirements.includes(
        PersonalInformationType.PhoneNumber
      ) &&
        !state.isPhoneNumberVerified) ||
      // 住所未登録
      (personalInformationRequirements.includes(
        PersonalInformationType.Address
      ) &&
        !state.address?.fullAddress) ||
      // ポイント不足
      (state.cart?.paymentType === 'prepaid_point' && !hasEnoughPoint) ||
      // 氏名未入力
      (purchaseType === 'konbini' && !watch('fullName')) ||
      // クレカ未登録
      (purchaseType === 'cash' && !state.paymentSource?.id) ||
      // コレクションの購入上限を超過した数がカートに入っている
      purchaseLimitPerPickupErrorIds.length > 0,
    [
      personalInformationRequirements,
      state.fullName,
      state.isPhoneNumberVerified,
      state.address?.fullAddress,
      state.cart?.paymentType,
      state.paymentSource?.id,
      hasEnoughPoint,
      purchaseType,
      watch('fullName'),
      purchaseLimitPerPickupErrorIds.length,
    ]
  )

  const displayResponsive = individualDisplayResponsive()

  useEffect(() => {
    reset({
      fullName: state.fullName,
      purchaseType: state.cart?.paymentType,
      receiptDateTimes:
        state.cartItemsByCollection?.map(({ eventCollection }) => {
          let pickupDate = ''
          let pickupTime = ''

          // 選択可能な受取時間をフィルタ
          const pickuptime = eventCollection?.pickupTime?.filter(
            ({ options }) =>
              dayjs(options.time)
                .add(eventCollection?.validMinutes ?? 0, 'minute')
                .valueOf() > dayjs().valueOf()
          )

          // 今すぐ受け取れる商品がある場合、初期値を設定
          if (
            calcurateIsJustNow(eventCollection, pickuptime?.[0]?.options?.time)
          ) {
            pickupDate = DateTime.formatDate(
              pickuptime?.[0].options?.time,
              'YYYY/MM/DD'
            ) as string
            pickupTime = DateTime.formatDate(
              pickuptime?.[0].options?.time,
              'HH:mm'
            ) as string
          }
          // コレクションの受け取り時間毎の購入上限のバリデーションも行う
          checkPurchaseLimitPerPickup({
            collectionId: eventCollection?.collectionId ?? '',
            date: pickupDate,
            hour: pickupTime,
            myPickupUsers: state.myPickupUsers,
            purchaseLimitPerPickup: eventCollection?.purchaseLimitPerPickup,
            cartItems: state.cartItems,
            message: t('purchaseLimitPerPickupTimeSelectError', {
              number: eventCollection?.purchaseLimitPerPickup,
            }),
          }).catch(e => {
            setPurchaseLimitPerPickupErrorIds(prev => [
              ...prev,
              eventCollection?.id ?? '',
            ])
          })

          // 選択枠が1のみの場合、初期値を設定
          if (eventCollection?.pickupTime?.length === 1) {
            pickupDate = DateTime.formatDate(
              eventCollection.pickupTime[0].options?.time,
              'YYYY/MM/DD'
            ) as string
            pickupTime = DateTime.formatDate(
              eventCollection.pickupTime[0].options?.time,
              'HH:mm'
            ) as string
          }
          return {
            hasPickupItem: !!eventCollection?.pickupTime?.length,
            collectionId: eventCollection?.pickupTime?.length
              ? eventCollection?.collectionId
              : '',
            pickupDate,
            pickupTime,
          }
        }) ?? [],
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.fullName, state.cartItemsByCollection?.length])

  return (
    <BaseLayout
      header={{
        back: true,
        defaultBackPage: generatePath(routerPages.StoreCartDetail, {
          cartId: state.cart?.id ?? '#',
        }),
        title: t('title'),
        ...(displayResponsive
          ? {
              responsive: true,
              maxWidth: 1120,
            }
          : {}),
      }}
      {...(displayResponsive
        ? {
            responsive: true,
            maxWidth: 1120,
          }
        : {})}
    >
      {!!personalInformationRequirements.length && (
        <ShippingAddressCell
          loading={state.loading}
          fullName={state.fullName}
          address={state.address}
          isPhoneNumberVerified={state.isPhoneNumberVerified}
          phoneNumber={state.phoneNumber}
          sendingVerification={state.sendingVerification}
          onSendVerificationSMS={onSendVerificationSMS}
          personalInformationRequirements={personalInformationRequirements}
          responsive={displayResponsive}
        />
      )}
      <Head title={routerNames.StoreItemPurchaseProceed} />
      <>
        {state.loading ? (
          <PaymentInfoCellSkeleton />
        ) : (
          <>
            {state.cart?.paymentType === 'prepaid_point' && (
              <PrepaidPointPaymentInfoCell
                hasEnoughPoint={hasEnoughPoint}
                formattedPrepaidPointBalance={
                  state.prepaidPointBalance?.balance ?? '0'
                }
                openDrawer={() => setOpenPointPurchaseDrawer(true)}
              />
            )}

            {state.cart?.paymentType === 'cash' && (
              <CashPaymentInfoCell
                paymentSource={state.paymentSource}
                value={purchaseType}
                setValue={setValue}
                error={errors.purchaseType?.message}
                konbiniFeeValue={state.konbiniFee?.fee}
                originFullNameValue={state.fullName}
                inputFullNameProps={{
                  ...register('fullName'),
                  error: errors.fullName,
                }}
              />
            )}
          </>
        )}
      </>

      <OrderDetailCell
        loading={state.loading}
        cartItems={state.cartItems}
        cartItemsByCollection={state.cartItemsByCollection}
        errors={errors.receiptDateTimes}
        setValue={setValue}
        watch={watch}
        paymentType={state.cart?.paymentType}
        purchaseType={purchaseType}
        systemFee={state.systemFee}
        shippingFee={state.shippingFee}
        konbiniFee={state.konbiniFee}
        totalPrice={state.cart?.totalPrice}
        formattedTotalPrice={state.cart?.formattedTotalPrice}
        link
        myPickupUsers={state.myPickupUsers}
        exceededItemCount={state.exceededItemCount}
        purchaseLimitPerPickupErrorIds={purchaseLimitPerPickupErrorIds}
        onRemovePurchaseLimitPerPickupErrorIds={(collectionId: string) =>
          setPurchaseLimitPerPickupErrorIds(
            purchaseLimitPerPickupErrorIds.filter(id => id !== collectionId)
          )
        }
        responsive={displayResponsive}
      />

      <ContentFrame>
        <Stack alignItems="center" sx={{ pb: 7 }}>
          {state.loading ? (
            <Skeleton
              variant="rectangular"
              sx={{ width: 244, height: 44, borderRadius: 34, mx: 'auto' }}
            />
          ) : (
            <Button
              size="large"
              sx={{ width: 212 }}
              loading={isSubmitting}
              onClick={handleSubmit(handleProceed)}
              disabled={disableToNext}
            >
              {t('toConfirmOrder', '確認画面に進む')}
            </Button>
          )}
        </Stack>
      </ContentFrame>
      {/* ポイントチャージ用ドロワー */}
      <PointChargeDrawer
        point={state.prepaidPointBalance?.balance ?? 0}
        onPurchase={(plan, paymentMethod) =>
          onPurchasePoint(plan, paymentMethod, {
            // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
            successUrl: `${window.location.origin}${generatePath(
              routerPages.StoreItemPurchaseProceed,
              { cartId: router.query.cartId }
            )}`,
            // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
            cancelUrl: `${window.location.origin}${generatePath(
              routerPages.StoreItemPurchaseProceed,
              { cartId: router.query.cartId }
            )}`,
            // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
            errorUrl: `${window.location.origin}${generatePath(
              routerPages.StoreItemPurchaseProceed,
              { cartId: router.query.cartId }
            )}`,
          })
        }
        open={openPointPurchaseDrawer}
        onClose={() => setOpenPointPurchaseDrawer(false)}
        loading={state.loading}
        chargeLoading={state.pointChargeLoading}
        purchasablePrepaidPoints={state.purchasablePrepaidPoints}
        paymentSource={state.paymentSource ?? null}
        enablePointCarrierPayment={state.enablePointCarrierPayment}
        pageName="StoreItemPurchaseProceed"
        responsive={displayResponsive}
      />
    </BaseLayout>
  )
}

type OrderDetailCellProps = {
  loading: boolean
  cartItems?: State['cartItems']
  cartItemsByCollection: State['cartItemsByCollection']
  errors?:
    | (
        | DeepMap<
            { collectionId: string; pickupDate: string; pickupTime: string },
            FieldError
          >
        | undefined
      )[]
    | undefined
  setValue?: UseFormSetValue<StoreItemPurchaseValue>
  watch?: UseFormWatch<StoreItemPurchaseValue>
  pickupTimes?:
    | {
        collectionId: string
        pickupTime: string | null
      }[]
    | null
  totalPrice?: UserCartStoreItemPurchaseProceedFragment['totalPrice']
  formattedTotalPrice?: UserCartStoreItemPurchaseProceedFragment['formattedTotalPrice']
  paymentType?: UserCartStoreItemPurchaseProceedFragment['paymentType']
  hiddenPaymentInfo?: boolean
  systemFee: Maybe<CollectionFeeDetailFragment>[]
  shippingFee: Maybe<FeeDetailFragment>
  konbiniFee: Maybe<FeeDetailFragment>
  purchaseType?: PurchaseType
  refundedAmount?: number
  myPickupUsers?: State['myPickupUsers']
  exceededItemCount?: State['exceededItemCount']
  link?: boolean
  purchaseLimitPerPickupErrorIds: string[]
  onRemovePurchaseLimitPerPickupErrorIds: (collectionId: string) => void
}
export const OrderDetailCell: Cell<
  OrderDetailCellProps & { responsive: boolean }
> = ({
  loading,
  cartItems,
  cartItemsByCollection,
  errors,
  setValue,
  watch,
  pickupTimes,
  hiddenPaymentInfo,
  myPickupUsers,
  exceededItemCount,
  purchaseLimitPerPickupErrorIds,
  onRemovePurchaseLimitPerPickupErrorIds,
  responsive,
  ...props
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')
  const { generatePath, routerPages } = useRouter()

  if (loading) return <OrderDetailCellSkeleton />

  return (
    <CellFrame sx={{ pb: 4 }}>
      <Stack sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}>
        <ContentFrame>
          <Typography variant="h5" color="text.secondary">
            {t('orderDetail', '注文内容')}
          </Typography>
        </ContentFrame>
      </Stack>
      <ContentFrame>
        <Stack spacing={3} sx={{ p: 5 }}>
          <Stack spacing={5}>
            {cartItemsByCollection?.map(
              ({ items, collection, eventCollection }, index) => {
                const pickupTime = pickupTimes?.find(
                  ({ collectionId }) =>
                    collectionId === eventCollection?.collectionId
                )?.pickupTime

                return (
                  <Stack spacing={4} key={index}>
                    <Stack spacing={2}>
                      {collection && (
                        <Typography variant="h6">
                          {collection?.title}
                        </Typography>
                      )}

                      {!!eventCollection?.pickupTime?.length &&
                        watch &&
                        setValue && (
                          <ReceiveTimeCell
                            validMinutes={Number(eventCollection.validMinutes)}
                            pickupTime={eventCollection.pickupTime?.filter(
                              ({ options }) =>
                                dayjs(options.time)
                                  .add(
                                    eventCollection.validMinutes
                                      ? eventCollection.validMinutes - 15
                                      : 0,
                                    'minute'
                                  )
                                  .valueOf() > dayjs().valueOf()
                            )}
                            eventCollection={eventCollection}
                            collectionId={collection?.id ?? ''}
                            collectionTitle={collection?.title ?? ''}
                            error={errors?.[index]?.pickupDate?.message}
                            value={watch(`receiptDateTimes.${index}`)}
                            onChange={value => {
                              setValue(`receiptDateTimes.${index}`, value, {
                                shouldValidate: true,
                              })
                              onRemovePurchaseLimitPerPickupErrorIds(
                                collection?.id ?? ''
                              )
                            }}
                            myPickupUsers={myPickupUsers}
                            cartItems={cartItems}
                            exceededItemCount={!!exceededItemCount}
                            purchaseLimitPerPickupErrorIds={
                              purchaseLimitPerPickupErrorIds
                            }
                            responsive={responsive}
                          />
                        )}
                      {!!eventCollection && !(watch && setValue) && pickupTime && (
                        <Typography variant="body2">
                          {t('receiptDateTime')}{' '}
                          {DateTime.formatStartEndTime(
                            pickupTime,
                            eventCollection?.validMinutes
                          )}
                        </Typography>
                      )}
                    </Stack>
                    {items.map((cartItem, _index) => (
                      <OrderItemCell
                        key={_index}
                        item={cartItem.item}
                        quantity={cartItem.count}
                      />
                    ))}
                  </Stack>
                )
              }
            )}
          </Stack>
          {!hiddenPaymentInfo && (
            <>
              <Divider />
              <TotalPaymentCell {...props} responsive={responsive} />
            </>
          )}
        </Stack>
      </ContentFrame>
    </CellFrame>
  )
}

export type TotalPaymentCellProps = Omit<
  OrderDetailCellProps,
  | 'cartItemsByCollection'
  | 'errors'
  | 'setValue'
  | 'pickupTimes'
  | 'watch'
  | 'loading'
  | 'myPickupUsers'
  | 'cartItems'
  | 'exceededItemCount'
  | 'purchaseLimitPerPickupErrorIds'
  | 'onRemovePurchaseLimitPerPickupErrorIds'
>
export const TotalPaymentCell: Cell<
  TotalPaymentCellProps & { responsive?: boolean }
> = ({
  paymentType,
  totalPrice,
  formattedTotalPrice,
  systemFee,
  shippingFee,
  konbiniFee,
  purchaseType,
  refundedAmount,
  responsive = false,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')
  const [open, setOpen] = useState<boolean>(false)

  const totalSystemFee = useMemo<number>(
    () => systemFee?.reduce((total, fee) => total + (fee?.fee ?? 0), 0) ?? 0,
    [systemFee]
  )

  const formattedTotalPriceFee = useMemo<string>(() => {
    const totalPriceFee = getTotalPriceFee({
      totalPrice,
      totalSystemFee,
      purchaseType,
      shippingFee: shippingFee?.fee,
      konbiniFee: konbiniFee?.fee,
    })
    return numeral(totalPriceFee).format('0,0')
  }, [totalPrice, shippingFee, konbiniFee, totalSystemFee, purchaseType])

  return (
    <CellFrame>
      <Stack spacing={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body2" color="text.secondary">
            {t('productPrice', '商品代金')}
          </Typography>
          {paymentType &&
            (paymentType === 'cash' ? (
              <Typography variant="body2">
                {t('itemPrice', '¥', { price: formattedTotalPrice })}
              </Typography>
            ) : (
              <PointDisplay size="small" point={formattedTotalPrice ?? 0} />
            ))}
        </Stack>
        {formattedTotalPrice !== formattedTotalPriceFee && (
          <Stack>
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {t('fee', '手数料')}
              </Typography>
              <IconButton.Info
                size="xSmall"
                sx={{ color: 'text.secondary' }}
                onClick={() => setOpen(true)}
              />
            </Stack>
            {systemFee?.map(
              fee =>
                fee && (
                  <Stack
                    key={fee.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" color="text.secondary">
                      {fee?.feeName ?? t('systemFee', 'システム手数料')}：
                    </Typography>
                    {paymentType === 'cash' ? (
                      <Typography variant="body2">
                        {t('itemPrice', '¥', {
                          price: numeral(fee.fee).format('0,0'),
                        })}
                      </Typography>
                    ) : (
                      <PointDisplay
                        size="small"
                        point={numeral(fee.fee).format('0,0')}
                      />
                    )}
                  </Stack>
                )
            )}
            {!!shippingFee && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="text.secondary">
                  {shippingFee?.feeName ?? t('deliveryFee', '配送料')}：
                </Typography>
                {paymentType === 'cash' ? (
                  <Typography variant="body2">
                    {t('itemPrice', '¥', { price: shippingFee?.fee ?? 0 })}
                  </Typography>
                ) : (
                  <PointDisplay size="small" point={shippingFee?.fee ?? 0} />
                )}
              </Stack>
            )}
            {!!konbiniFee && purchaseType === 'konbini' && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="text.secondary">
                  {konbiniFee?.feeName ??
                    t('konbiniFee', '事務手数料（コンビニ決済）')}
                  ：
                </Typography>
                {paymentType === 'cash' ? (
                  <Typography variant="body2">
                    {t('itemPrice', '¥', { price: konbiniFee?.fee ?? 0 })}
                  </Typography>
                ) : (
                  <PointDisplay size="small" point={konbiniFee?.fee ?? 0} />
                )}
              </Stack>
            )}
          </Stack>
        )}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6">
            {purchaseType === 'konbini' || purchaseType === 'cash'
              ? t('paymentAmount', '支払い金額')
              : t('paymentAmountPoint', '合計ポイント')}
          </Typography>
          {paymentType &&
            (paymentType === 'cash' ? (
              <Typography
                variant="h6"
                sx={{
                  textDecoration: refundedAmount ? 'line-through' : 'unset',
                }}
              >
                {t('itemPrice', '¥', {
                  price: formattedTotalPriceFee,
                })}
              </Typography>
            ) : (
              <PointDisplay
                point={formattedTotalPriceFee ?? 0}
                sx={{
                  textDecoration: refundedAmount ? 'line-through' : 'unset',
                }}
              />
            ))}
        </Stack>
        {!!refundedAmount && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">
              {paymentType === 'cash'
                ? t('refundAmount', '返金金額')
                : t('refundPoints', '返金ポイント')}
            </Typography>
            {paymentType === 'cash' ? (
              <Typography variant="h6">
                {t('itemPrice', '¥', {
                  price: refundedAmount,
                })}
              </Typography>
            ) : (
              <PointDisplay point={refundedAmount ?? 0} />
            )}
          </Stack>
        )}
      </Stack>
      <CommissionModal
        open={open}
        onClose={() => setOpen(false)}
        paymentType={paymentType}
        shippingFee={shippingFee ?? null}
        systemFee={systemFee}
        konbiniFee={purchaseType === 'konbini' ? konbiniFee : null}
        responsive={responsive}
      />
    </CellFrame>
  )
}

type ReceiveTimeCellProps = {
  pickupTime: EventCollectionBoxFragment['pickupTime']
  collectionId: string
  collectionTitle: string
  value: {
    collectionId: string
    pickupDate?: string
    pickupTime?: string
  }
  error?: string
  onChange: (value: {
    collectionId: string
    pickupDate: string
    pickupTime: string
  }) => void
  validMinutes: number
  myPickupUsers?: State['myPickupUsers']
  cartItems?: State['cartItems']
  eventCollection: UserCartStoreItemProceedEventCollectionFragment | undefined
  exceededItemCount: State['exceededItemCount']
  purchaseLimitPerPickupErrorIds: string[]
  responsive: boolean
}
const ReceiveTimeCell: Cell<ReceiveTimeCellProps> = ({
  pickupTime,
  collectionId,
  collectionTitle,
  value,
  error,
  onChange,
  validMinutes,
  myPickupUsers,
  cartItems,
  eventCollection,
  exceededItemCount,
  purchaseLimitPerPickupErrorIds,
  responsive,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')
  const [open, setOpen] = useState<boolean>(false)

  // NOTE: 購入手続き中に時間外になる可能性があるのでAPIのバリデーション(10分)より5分余分にチェックする
  const pickupNumber =
    pickupTime?.filter(
      ({ options }) =>
        dayjs(options.time)
          .add(validMinutes - 15, 'minute')
          .valueOf() > dayjs().valueOf()
    ).length ?? 0

  // 購入期限を過ぎ、選択可能な受取時間がない場合の判定
  const pickable = !!pickupNumber

  let buttonText = value?.pickupTime
    ? (DateTime.formatStartEndTime(
        `${value.pickupDate} ${value.pickupTime}`,
        validMinutes
      ) as string)
    : t('pleaseSelectDateTime', '日時を選択してください')

  if (!pickable) {
    buttonText = t('unpickable')
  } else if (exceededItemCount) {
    buttonText = t('purchaseLimitError')
  } else if (purchaseLimitPerPickupErrorIds.includes(collectionId)) {
    buttonText = t('purchaseLimitPerPickupTimeError', {
      number: eventCollection?.purchaseLimitPerPickup,
    })
  }

  return (
    <CellFrame>
      <Button
        fullWidth
        variant="outlined"
        endIcon={<Icons.RightArrow />}
        sx={{
          borderRadius: 2,
          borderColor: error ? 'error.main' : 'grey.400',
          '&.Mui-disabled': {
            bgcolor: 'background.default',
            borderColor: error ? 'error.main' : 'grey.400',
          },
        }}
        onClick={() => setOpen(true)}
        disabled={!pickable || pickupNumber === 1 || exceededItemCount}
      >
        <Stack flex={1} alignItems="flex-start" spacing={1}>
          <Typography variant="body2">{t('receiptDateTime')}</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {calcurateIsJustNow(
              eventCollection,
              Number(
                dayjs(`${value?.pickupDate} ${value?.pickupTime}`).valueOf()
              )
            ) && <JustNowChip />}
            <Typography
              variant="button"
              color={
                error ||
                exceededItemCount ||
                purchaseLimitPerPickupErrorIds.includes(collectionId ?? '')
                  ? 'error.main'
                  : pickable
                  ? 'text.primary'
                  : 'text.disabled'
              }
              textAlign="left"
            >
              {buttonText}
            </Typography>
          </Stack>
        </Stack>
      </Button>
      <ReceiveTimeModal
        pickupTime={pickupTime}
        open={open}
        value={value as ReceiveTimeValue}
        onClose={() => setOpen(false)}
        collectionId={collectionId}
        collectionTitle={collectionTitle}
        validMinutes={validMinutes}
        onSubmit={onChange}
        myPickupUsers={myPickupUsers}
        cartItems={cartItems}
        eventCollection={eventCollection}
        {...(responsive
          ? {
              sx: {
                '& .MuiPaper-root': {
                  maxWidth: '100%',
                },
              },
            }
          : {})}
      />
    </CellFrame>
  )
}

type OrderItemCellProps = {
  item?: Pick<
    State['cartItems'][number]['item'],
    'title' | 'thumbnail' | 'sku' | 'formattedPrice' | 'paymentType'
  >
  quantity: number
  href?: string
}
export const OrderItemCell: Cell<OrderItemCellProps> = ({
  item,
  quantity,
  href,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')

  const formattedPrice = useMemo(() => {
    if (item?.sku?.price) {
      return item?.sku?.formattedPrice ?? '0'
    }
    return item?.formattedPrice ?? '0'
  }, [item?.formattedPrice, item?.sku?.formattedPrice, item?.sku?.price])

  if (!item) return null

  // クリック時の遷移先が指定されている場合はリンクを設定
  const Wrapper = ({ children }: PropsWithChildren<{}>) =>
    href ? (
      <Link href={href} sx={{ width: 'fit-content' }}>
        {children}
      </Link>
    ) : (
      <>{children}</>
    )

  return (
    <Stack direction="row" spacing={3}>
      {item.thumbnail.front?.smallUri ? (
        <Wrapper>
          <ItemImageArea size="small" src={item.thumbnail.front.smallUri} />
        </Wrapper>
      ) : (
        <Skeleton variant="rectangular" width={60} height={96} />
      )}
      <Stack flex={1} spacing={1} sx={{ minWidth: 0, pt: 4 }}>
        <Wrapper>
          <Stack>
            <Stack
              direction="row"
              alignItems="center"
              gap={4}
              sx={{
                pt: !item.sku?.unitName ? 2 : 0,
              }}
            >
              <Typography variant="subtitle1" sx={styleSets.lineCamp(2)}>
                {item.title}
              </Typography>
              {!!href && (
                <Icons.RightArrow fontSize="xSmall" sx={{ mb: '3px' }} />
              )}
            </Stack>

            {!!item.sku && (
              <Typography variant="caption">{item.sku?.unitName}</Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={2}>
            {item.paymentType === 'cash' ? (
              <Typography variant="subtitle1">
                {t('itemPrice', '¥', { price: formattedPrice })}
              </Typography>
            ) : (
              <PointDisplay size="small" point={formattedPrice} />
            )}
            <Typography variant="subtitle1">
              {t('itemQuantity', '数量', { quantity })}
            </Typography>
          </Stack>
        </Wrapper>
      </Stack>
    </Stack>
  )
}

type ShippingAddressCellProps = {
  loading: boolean
  fullName: State['fullName']
  address: State['address']
  isPhoneNumberVerified: State['isPhoneNumberVerified']
  phoneNumber: State['phoneNumber']
  hiddenLink?: boolean
  sendingVerification: boolean
  onSendVerificationSMS: Action['onSendVerificationSMS']
  personalInformationRequirements: string[]
  responsive: boolean
}
export const ShippingAddressCell: Cell<ShippingAddressCellProps> = ({
  loading,
  fullName,
  address,
  isPhoneNumberVerified,
  phoneNumber,
  hiddenLink,
  sendingVerification,
  onSendVerificationSMS,
  personalInformationRequirements,
  responsive,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')
  const { routerPages } = useRouter()
  const { analytics } = useAnalytics()

  if (loading) {
    return <ShippingAddressCellSkeleton />
  }

  return (
    <CellFrame sx={{ pt: 3, pb: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 5,
          bgcolor: 'grey.50',
          py: 2,
          alignItems: responsive ? 'center' : undefined,
        }}
      >
        <ContentFrame
          {...(responsive
            ? {
                sx: {
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
              }
            : {})}
        >
          <Typography variant="h5" color="text.secondary">
            {t('personalInfo', 'お客様情報')}
          </Typography>
          {!hiddenLink && (
            <Link
              variant="button"
              href={
                personalInformationRequirements.includes(
                  PersonalInformationType.Address
                )
                  ? routerPages.DeliveryInfoSetting
                  : routerPages.StoreUserInfo
              }
              onClick={() => {
                analytics?.track('click_registerShippingInfo', {
                  applicationKey: config.applicationKey,
                  type: address?.fullAddress ? 'update' : 'register',
                })
              }}
            >
              {t('change', '変更する')}
            </Link>
          )}
        </ContentFrame>
      </Stack>
      <ContentFrame>
        <Stack sx={{ px: 5, py: 3 }}>
          {personalInformationRequirements.includes(
            PersonalInformationType.FullName
          ) && (
            <Stack spacing={1} sx={{ pt: 0 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {t('familyName', '氏名')}
              </Typography>
              <Typography
                variant="body2"
                color={fullName ? 'text.primary' : 'text.hint'}
              >
                {fullName ??
                  t('unregisteredFullName', '氏名が登録されていません')}
              </Typography>
            </Stack>
          )}
          {personalInformationRequirements.includes(
            PersonalInformationType.Address
          ) && (
            <Stack spacing={1} sx={{ pt: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {t('address')}
              </Typography>
              <Typography
                variant="body2"
                color={address?.fullAddress ? 'text.primary' : 'text.hint'}
              >
                {address?.fullAddress ?? t('unregisteredAddress')}
              </Typography>
            </Stack>
          )}
          {personalInformationRequirements.includes(
            PersonalInformationType.PhoneNumber
          ) && (
            <Stack spacing={1} sx={{ pt: 2 }}>
              <Stack direction="row" spacing={3} alignItems="center">
                <Typography variant="subtitle1" color="text.secondary">
                  {t('phoneNumber')}
                </Typography>
                {!isPhoneNumberVerified ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icons.Error
                      fontSize="small"
                      sx={{ color: 'error.main', mt: -1 }}
                    />
                    <Typography variant="body2" color="error.main">
                      {t('unverify')}
                    </Typography>
                    {phoneNumber && (
                      <Button
                        variant="text"
                        color="primary"
                        loading={sendingVerification}
                        onClick={onSendVerificationSMS}
                      >
                        {t('verify')}
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icons.Success
                      fontSize="small"
                      sx={{ color: 'text.hint', mt: -1 }}
                    />
                    <Typography variant="body2" color="text.hint">
                      {t('verified')}
                    </Typography>
                  </Stack>
                )}
              </Stack>
              <Typography variant="body2">{phoneNumber}</Typography>
            </Stack>
          )}
        </Stack>
      </ContentFrame>
    </CellFrame>
  )
}

type PrepaidPointPaymentInfoCellProps = {
  hasEnoughPoint: boolean
  formattedPrepaidPointBalance: string | number
  openDrawer: () => void
}

export const PrepaidPointPaymentInfoCell: Cell<PrepaidPointPaymentInfoCellProps> =
  ({ hasEnoughPoint, formattedPrepaidPointBalance, openDrawer }) => {
    const { t } = useTranslation('storeItemPurchaseProceed')

    return (
      <CellFrame sx={{ pb: 4 }}>
        <Stack sx={{ px: 5, py: 2, bgcolor: 'grey.50' }}>
          <ContentFrame>
            <Typography variant="h5" color="text.secondary">
              {t('paymentInfo', '支払い情報')}
            </Typography>
          </ContentFrame>
        </Stack>
        <ContentFrame>
          <Stack sx={{ px: 5, py: 3 }}>
            {!hasEnoughPoint && (
              <Typography variant="body2" color="error.main">
                {t('noEnoughPointContent', 'ポイント残高が不足しています。')}
              </Typography>
            )}
            <Stack direction="row" justifyContent="space-between">
              <Stack spacing={1}>
                <Typography variant="h5">
                  {t('purchaseByPoint', 'ポイントで購入')}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('pointBalance', 'ポイント残高')}
                  </Typography>
                  <PointDisplay point={formattedPrepaidPointBalance} />
                </Stack>
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button size="small" onClick={openDrawer}>
                  {t('charge', 'チャージ')}
                </Button>
              </Box>
            </Stack>
          </Stack>
        </ContentFrame>
      </CellFrame>
    )
  }

type CashPaymentInfoCellProps = {
  value: string
  setValue: UseFormSetValue<StoreItemPurchaseValue>
  paymentSource: Maybe<PaymentDetailFragment> | undefined
  originFullNameValue: string
  error: string | undefined
  inputFullNameProps: Omit<TextInputFieldProps, 't' | 'type'>
  konbiniFeeValue: number | undefined
}
const CashPaymentInfoCell: Cell<CashPaymentInfoCellProps> = ({
  paymentSource,
  value,
  setValue,
  originFullNameValue,
  error,
  inputFullNameProps,
  konbiniFeeValue,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')

  return (
    <CellFrame sx={{ pb: 4 }}>
      <Stack sx={{ px: 5, py: 2, bgcolor: 'grey.50' }}>
        <ContentFrame>
          <Typography variant="h5" color="text.secondary">
            {t('paymentInfo', '支払い情報')}
          </Typography>
        </ContentFrame>
      </Stack>
      <ContentFrame>
        <Stack sx={{ px: 5, py: 3 }}>
          {!config.modules.konbiniPayment ? (
            <PaymentByCashLabel paymentSource={paymentSource} />
          ) : (
            <FormControl fullWidth sx={{ minHeight: 'unset' }}>
              <RadioGroup
                value={value}
                onChange={(_, value) => {
                  setValue('purchaseType', value as PurchaseType, {
                    shouldValidate: true,
                  })
                  if (value === 'konbini') {
                    setValue('fullName', originFullNameValue, {
                      shouldValidate: true,
                    })
                  }
                }}
                sx={{ gap: theme => theme.spacing(4) }}
              >
                <FormControlLabel
                  value="cash"
                  checked={value === 'cash'}
                  control={<Radio />}
                  label={<PaymentByCashLabel paymentSource={paymentSource} />}
                />
                <Divider />
                <FormControlLabel
                  value="konbini"
                  checked={value === 'konbini'}
                  control={<Radio />}
                  label={
                    <Stack spacing={1}>
                      <Stack direction="row" gap={2} alignItems="baseline">
                        <Typography variant="h5">
                          {t('convenienceStore', 'コンビニ決済')}
                        </Typography>
                        <Typography variant="body2">
                          {t('convenienceStoreNote', {
                            paymentFee: konbiniFeeValue,
                          })}
                        </Typography>
                      </Stack>
                      <Typography variant="caption">
                        {t('convenienceStoreDesc')}
                      </Typography>
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}
          {value === 'konbini' && (
            <>
              <Typography
                variant="caption"
                sx={{ pt: 4 }}
                color="text.secondary"
              >
                {t('storeItemNote')}
              </Typography>
              <TextInputCell type="text" {...inputFullNameProps} />
            </>
          )}
        </Stack>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      </ContentFrame>
    </CellFrame>
  )
}

type PaymentByCashLabelProps = {
  paymentSource: Maybe<PaymentDetailFragment> | undefined
}
const PaymentByCashLabel: Cell<PaymentByCashLabelProps> = ({
  paymentSource,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')
  const { routerPages } = useRouter()

  if (paymentSource) {
    return (
      <Stack direction="row" spacing={3} alignItems="center">
        <StorageImage
          height={25}
          src={generateCreditCardLogoPath(paymentSource.brand)}
          target="common"
        />
        <Typography variant="h6">
          {t('last4Digits', { number: paymentSource.last4 })}
        </Typography>
      </Stack>
    )
  }
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h6">{t('creditCard')}</Typography>
        <Link variant="button" href={routerPages.PaymentMethodSetting}>
          {t('register', '登録')}
        </Link>
      </Stack>
      <Typography variant="caption" color="error.main">
        {t('registerNote', '支払い情報が登録されていません')}
      </Typography>
    </Stack>
  )
}

const TextInputCell = forwardRef(
  (
    {
      titleProps,
      textFieldProps,
      error,
      ...props
    }: Omit<TextInputFieldProps, 't'>,
    ref
  ) => {
    const { t } = useTranslation('storeItemPurchaseProceed')
    return (
      <CellFrame sx={{ pt: 4 }}>
        <TextInputField
          titleProps={{
            ...titleProps,
            variant: 'subtitle1',
            color: 'text.primary',
          }}
          textFieldProps={{
            label: error ? t('error') : '',
            ...textFieldProps,
            InputProps: {
              inputProps: { sx: { p: '14px' } },
            },
            FormHelperTextProps: {
              sx: { bgcolor: 'transparent' },
            },
            sx: { mb: 0, backgroundColor: 'transparent' },
          }}
          ref={ref}
          {...props}
          error={error}
          t={t}
        />
      </CellFrame>
    )
  }
)

const ShippingAddressCellSkeleton: Cell = () => (
  <CellFrame sx={{ pb: 5 }}>
    <Box sx={{ px: 5, py: 2, bgcolor: 'grey.50' }}>
      <SkeletonText variant="h3" width="50%" />
    </Box>
    <ContentFrame>
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{ px: 5, py: 3 }}
      >
        <Skeleton variant="circular" sx={{ width: 40, height: 40 }} />
        <Stack flex={1}>
          <SkeletonText variant="small" width="75%" />
          <Stack sx={{ pt: 2 }}>
            <SkeletonText variant="small" width="85%" />
            <SkeletonText variant="small" width="85%" />
          </Stack>
        </Stack>
      </Stack>
    </ContentFrame>
  </CellFrame>
)

const PaymentInfoCellSkeleton: Cell = () => {
  return (
    <CellFrame sx={{ pb: 4 }}>
      <Stack sx={{ px: 5, py: 2, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h3" width="50%" />
      </Stack>
      <ContentFrame>
        <Stack sx={{ px: 5, py: 3 }} spacing={7}>
          <SkeletonText sx={{ mt: 4 }} variant="large" width="40%" />
          <Divider />
          <SkeletonText sx={{ mt: 4 }} variant="large" width="40%" />
        </Stack>
      </ContentFrame>
    </CellFrame>
  )
}

const OrderDetailCellSkeleton: Cell = () => (
  <CellFrame sx={{ pb: 5 }}>
    <Box sx={{ py: 2, px: 5, bgcolor: 'grey.50' }}>
      <ContentFrame>
        <SkeletonText variant="h3" width="50%" />
      </ContentFrame>
    </Box>
    <ContentFrame>
      <Stack sx={{ p: 5, py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={3} sx={{ pb: 5 }}>
          <Skeleton variant="rectangular" sx={{ width: 60, height: 96 }} />
          <Stack flex={1}>
            <SkeletonText variant="large" width="100%" />
            <SkeletonText variant="small" width="100%" />
            <Stack direction="row-reverse" sx={{ pt: 4 }}>
              <SkeletonText variant="large" width="40%" />
            </Stack>
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between" sx={{ pt: 2 }}>
          <SkeletonText variant="large" width="30%" />
          <SkeletonText variant="large" width="30%" />
        </Stack>
      </Stack>
    </ContentFrame>
  </CellFrame>
)

type CommissionModalProps = {
  open: boolean
  onClose: () => void
  paymentType?: UserPaymentType
  systemFee: Maybe<CollectionFeeDetailFragment>[]
  shippingFee: Maybe<FeeDetailFragment>
  konbiniFee: Maybe<FeeDetailFragment>
  responsive: boolean
}
export const CommissionModal: Cell<CommissionModalProps> = ({
  open,
  onClose,
  paymentType,
  systemFee,
  shippingFee,
  konbiniFee,
  responsive,
}) => {
  const { t } = useTranslation('storeItemPurchaseProceed')

  const totalSystemFee = useMemo<number>(
    () => systemFee?.reduce((total, fee) => total + (fee?.fee ?? 0), 0) ?? 0,
    [systemFee]
  )

  const formattedTotalPrice = useMemo(
    () =>
      numeral(
        totalSystemFee + (shippingFee?.fee ?? 0) + (konbiniFee?.fee ?? 0)
      ).format('0,0'),
    [totalSystemFee, shippingFee, konbiniFee]
  )

  const commissions = [
    ...systemFee.map(fee => ({
      title: t('jointFoodFee', { name: fee?.collectionFee?.title }),
      label: fee?.feeName,
      price: fee?.fee,
      hidden: !fee?.fee,
      note: fee?.feeDescription,
    })),
    {
      title: t('otherFee', 'その他手数料'),
      label: konbiniFee?.feeName,
      price: konbiniFee?.fee,
      hidden: !konbiniFee,
      note: konbiniFee?.feeDescription,
    },
    {
      title: '',
      label: shippingFee?.feeName,
      price: shippingFee?.fee,
      hidden: !shippingFee,
      note: shippingFee?.feeDescription,
    },
  ]

  return (
    <Drawer
      open={open}
      onClose={onClose}
      {...(responsive
        ? {
            sx: {
              '& .MuiPaper-root': {
                maxWidth: '100%',
              },
            },
          }
        : {})}
    >
      <ContentFrame>
        <Stack sx={{ p: 5 }} direction="row" alignItems="center">
          <Typography
            variant="h6"
            align="center"
            sx={{ width: '100%', ml: '30px' }}
          >
            {t('feeBreakdown', '手数料の内訳')}
          </Typography>
          <IconButton.Close edge="end" size="small" onClick={onClose} />
        </Stack>
        <Stack sx={{ px: 5, pb: 5 }} spacing={2}>
          {filterHiddenItem(commissions).map(
            (commission, index) =>
              commission && (
                <Fragment key={index}>
                  <Stack spacing={1}>
                    {!!commission?.title && (
                      <Typography variant="subtitle1">
                        {commission.title}
                      </Typography>
                    )}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ pt: 1 }}
                    >
                      <Typography variant="body2">
                        {commission.label}
                      </Typography>
                      {paymentType === UserPaymentType.Cash ? (
                        <Typography variant="body2">
                          ￥{numeral(commission.price).format('0,0')}
                        </Typography>
                      ) : (
                        <PointDisplay
                          size="small"
                          point={commission.price ?? 0}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption">{commission.note}</Typography>
                  </Stack>
                  <Divider />
                </Fragment>
              )
          )}
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">{t('total')}</Typography>
            {paymentType === UserPaymentType.Cash ? (
              <Typography variant="body2">￥{formattedTotalPrice}</Typography>
            ) : (
              <PointDisplay size="small" point={formattedTotalPrice} />
            )}
          </Stack>
          <Stack alignItems="center">
            <Button size="large" sx={{ width: 212 }} onClick={onClose}>
              {t('ok')}
            </Button>
          </Stack>
        </Stack>
      </ContentFrame>
    </Drawer>
  )
}

function getTotalPriceFee({
  totalPrice,
  totalSystemFee,
  shippingFee,
  purchaseType,
  konbiniFee,
}: {
  totalPrice?: number
  totalSystemFee?: number
  shippingFee?: number
  purchaseType?: string
  konbiniFee?: number
}) {
  if (!totalPrice) {
    return 0
  }
  return (
    (totalPrice ?? 0) +
    (totalSystemFee ?? 0) +
    (shippingFee ?? 0) +
    (purchaseType === 'konbini' ? konbiniFee ?? 0 : 0)
  )
}
